import { useCallback, useEffect, useRef, useState, type DragEvent, type ChangeEvent } from 'react'
import { Upload } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

type EPlanUploadDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onFileSelect: (file: File) => void | Promise<void>
}

function isAllowedEPlanFile(file: File): boolean {
  const lowerCaseFileName = file.name.toLowerCase()
  return (
    file.type === 'application/pdf' ||
    file.type === 'image/jpeg' ||
    file.type === 'image/png' ||
    lowerCaseFileName.endsWith('.pdf') ||
    lowerCaseFileName.endsWith('.jpg') ||
    lowerCaseFileName.endsWith('.jpeg') ||
    lowerCaseFileName.endsWith('.png')
  )
}

export function EPlanUploadDialog({
  open,
  onOpenChange,
  onFileSelect,
}: EPlanUploadDialogProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) {
      setIsUploading(false)
    }
  }, [open])

  const handleFiles = useCallback(
    async (files: File[]) => {
      const sourceFile = files.find((file) => isAllowedEPlanFile(file))
      if (sourceFile == null) {
        toast.error(t('settings.ePlan.uploadDialog.allowedFileTypes'))
        return
      }
      setIsUploading(true)
      try {
        await onFileSelect(sourceFile)
        onOpenChange(false)
      } finally {
        setIsUploading(false)
      }
    },
    [onFileSelect, onOpenChange, t]
  )

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      if (isUploading) {
        return
      }
      const file = event.dataTransfer.files.item(0)
      if (file != null) {
        void handleFiles([file])
      }
    },
    [handleFiles, isUploading]
  )

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.item(0)
      if (file != null) {
        void handleFiles([file])
      }
      event.target.value = ''
    },
    [handleFiles]
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>{t('settings.ePlan.uploadDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('settings.ePlan.uploadDialog.description')}
          </DialogDescription>
        </DialogHeader>

        <div
          className={cn(
            'flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed p-8 text-center',
            isUploading && 'pointer-events-none opacity-60'
          )}
          onDragOver={(event) => event.preventDefault()}
          onDrop={handleDrop}
        >
          <Upload className='size-8 text-muted-foreground' />
          <p className='text-sm text-muted-foreground'>
            {t('settings.ePlan.uploadDialog.dropHint')}
          </p>
          <Button
            type='button'
            variant='outline'
            disabled={isUploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {t('settings.ePlan.uploadDialog.chooseFile')}
          </Button>
          <input
            ref={fileInputRef}
            type='file'
            accept='application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png'
            className='hidden'
            onChange={handleInputChange}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}
